import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Lock, Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAdminAuth } from "../auth/AdminAuthContext";
import PasswordInput from "../components/PasswordInput";
import Logo from "../components/Logo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Có lỗi xảy ra. Vui lòng thử lại.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAdminAuth();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/admin/nail-events";

  const [email, setEmail] = useState(() => localStorage.getItem("nailViet.adminRememberedEmail") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem("nailViet.adminRememberedEmail"));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      // Persist / clear remembered email based on the checkbox state.
      if (remember) {
        localStorage.setItem("nailViet.adminRememberedEmail", email.trim());
      } else {
        localStorage.removeItem("nailViet.adminRememberedEmail");
      }
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Đăng nhập thất bại.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-linen relative overflow-hidden" data-testid="admin-login-page">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #0E5B4F 0%, transparent 40%), radial-gradient(circle at 80% 80%, #C9A14A 0%, transparent 40%)" }} />
      <div className="pointer-events-none absolute -top-20 -left-20 w-72 h-72 rounded-full bg-emerald2/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />

      <header className="relative z-10 px-5 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-espresso-muted hover:text-espresso text-sm">
          <ArrowLeft className="w-4 h-4" /> Trang chủ
        </Link>
        <Logo dark={false} variant="full" to="/" />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 sm:px-6 py-6 sm:py-10">
        <div className="w-full max-w-md">
          {/* Hero pill */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-espresso/10 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] font-semibold text-espresso">
              <ShieldCheck className="w-3.5 h-3.5 text-gold" /> Khu vực quản trị
            </div>
            <h1 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-espresso leading-tight">Đăng nhập</h1>
            <p className="mt-2 text-sm text-warmgray-700">Sử dụng tài khoản quản trị để tiếp tục.</p>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(14,91,79,0.25)] border border-white/60 p-6 sm:p-8 space-y-4"
            data-testid="admin-login-form"
          >
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">Email</span>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-warmgray-500" />
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admins@nailvietclub.com"
                  data-testid="admin-login-email-input"
                  className="w-full bg-white border border-warmgray-200 rounded-xl pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40 focus:border-emerald2/40"
                />
              </div>
            </label>

            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">Mật khẩu</span>
              <PasswordInput
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-login-password-input"
                testIdSuffix="admin-login-password"
                leftIcon={<Lock className="w-4 h-4 text-warmgray-500" />}
                className="w-full bg-white border border-warmgray-200 rounded-xl py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40 focus:border-emerald2/40"
              />
            </label>

            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-warmgray-700 cursor-pointer select-none" data-testid="admin-login-remember-label">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="admin-login-remember-checkbox"
                  className="w-4 h-4 accent-emerald2 rounded"
                />
                <span>Ghi nhớ email trên thiết bị này</span>
              </label>
              <Link to="/admin/reset-password" data-testid="admin-forgot-password-inline" className="text-xs text-espresso-muted hover:text-espresso underline underline-offset-4">
                Quên mật khẩu?
              </Link>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-linen rounded-full px-5 py-3 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60 transition"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {loading ? "Đang đăng nhập…" : "Đăng nhập"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/admin/reset-password" data-testid="admin-forgot-password-link" className="text-xs text-espresso-muted hover:text-espresso underline underline-offset-4">
              Quên mật khẩu?
            </Link>
          </div>

          <p className="mt-6 text-center text-xs text-espresso-muted">Bảo mật bằng JWT · Phiên đăng nhập 7 ngày</p>
        </div>
      </main>
    </div>
  );
}
